import { readJson } from "fs-extra";
import { pickBy } from "lodash";

import { writeFileAsync } from "../Commons/FsUtils";
import { ILogger } from "../Commons/Logging/ILogger";
import { getLogger } from "../Commons/Logging/StaticLogger";
import { extractGfvFromFormPath } from "../../../Common/ExtractGFVPath/ExtractGFVPath";

import {
    getLastStableEngineVersion,
    getLastStableGeneratorVersion,
    getPrereleaseVersionFormJson,
} from "./PublishedVersionUtils";
import { CandyVersionSpec, isValidSemVerVersionRange, parseCandyVersionSpec } from "./Version";

export enum FormBuildMode {
    NormalBuild,
    NoEditor,
}

export interface FormInfo {
    themes?: string[];
    mode: FormBuildMode;
    formName: string;
    formSourcesPath: string;
    generatorVersion: undefined | CandyVersionSpec;
    engineVersion: undefined | CandyVersionSpec;
    useServerSideNormalization: boolean;
}

export interface FormConfigContent {
    themes?: string[];
    build?: "ci" | "manual";
    mode?: "no-editor";
    dependencies?: {
        generator?: string;
        engine?: string;
    };
    settings?: {
        useServerSideFLangNormalization?: boolean;
        autoCalculationCalculatorImpl?: "calculator2";
        disableFlangRulesForEnableAutoFieldFeature?: boolean;
        disableFlangRulesForFormulaAndInitNormalizer?: boolean;
    };
}

interface FormConfig {
    themes?: string[];
    build?: "ci" | "manual";
    mode?: "no-editor" | "normal-build";
    dependencies?: {
        generator: undefined | string;
        engine: undefined | string;
    };
    settings: {
        useServerSideFLangNormalization: boolean;
        autoCalculationCalculatorImpl?: "calculator2" | "default";
        disableFlangRulesForEnableAutoFieldFeature?: boolean;
        disableFlangRulesForFormulaAndInitNormalizer?: boolean;
    };
}

export async function resolveFormInfo(
    formJsonFile: string,
    pathToForm: string,
    logger: ILogger = getLogger(),
    upgradeFormJson: boolean = false,
    useLastStableVersion: boolean = false,
    usePrereleaseVersion?: string,
    nightly: boolean = false
): Promise<FormInfo> {
    const formConfig = await readFormConfig(formJsonFile);
    if (useLastStableVersion && usePrereleaseVersion != undefined) {
        throw new Error("Необходимо использовать либо флаг useLastStableVersion либо usePrereleaseVersion");
    }
    if (useLastStableVersion) {
        formConfig.dependencies = {
            engine: await getLastStableEngineVersion(),
            generator: await getLastStableGeneratorVersion(),
        };
        logger.log(`Updated to last stable version ${formConfig.dependencies.engine}`);
        await writeFileAsync(formJsonFile, JSON.stringify(formConfig, undefined, "  "), "utf8");
    }
    if (usePrereleaseVersion != undefined) {
        const prereleaseVersionFormJson = await getPrereleaseVersionFormJson(usePrereleaseVersion);
        if (prereleaseVersionFormJson != undefined) {
            formConfig.dependencies = {
                engine: prereleaseVersionFormJson,
                generator: prereleaseVersionFormJson,
            };
            logger.log(`Updated to last prerelease version ${formConfig.dependencies.engine}`);
        }
        await writeFileAsync(formJsonFile, JSON.stringify(formConfig, undefined, "  "), "utf8");
    }
    let generatorVersion =
        formConfig.dependencies?.generator != undefined
            ? parseCandyVersionSpec(formConfig.dependencies.generator)
            : undefined;
    let engineVersion =
        formConfig.dependencies?.engine != undefined
            ? parseCandyVersionSpec(formConfig.dependencies.engine)
            : undefined;
    if (nightly) {
        generatorVersion = { type: "pre", preId: "nightly" };
        engineVersion = { type: "pre", preId: "nightly" };
    }
    if (upgradeFormJson) {
        await writeFileAsync(formJsonFile, JSON.stringify(formConfig, undefined, "  "), "utf8");
    }
    return {
        themes: formConfig.themes,
        mode: formConfig.mode === "no-editor" ? FormBuildMode.NoEditor : FormBuildMode.NormalBuild,
        formName: extractGfvFromFormPath(pathToForm),
        formSourcesPath: pathToForm,
        generatorVersion: generatorVersion,
        engineVersion: engineVersion,
        useServerSideNormalization: formConfig.settings.useServerSideFLangNormalization ?? false,
    };
}

export async function isNewFormJsonFormat(formJsonFile: string): Promise<boolean> {
    const formConfig: FormConfigContent = await readJson(formJsonFile);
    return formConfig.dependencies != undefined;
}

export async function readFormConfig(formJsonFile: string): Promise<FormConfig> {
    const formConfig: FormConfigContent = await readJson(formJsonFile);
    return getFormConfigFromSource(formConfig);
}

export function getFormConfigFromSource(formConfig: FormConfigContent): FormConfig {
    if (
        formConfig.dependencies?.generator != undefined &&
        !isValidSemVerVersionRange(formConfig.dependencies.generator)
    ) {
        throw new Error(`${formConfig.dependencies.generator} is not valid version format`);
    }

    if (formConfig.dependencies?.engine != undefined && !isValidSemVerVersionRange(formConfig.dependencies.engine)) {
        throw new Error(`${formConfig.dependencies.engine} is not valid version format`);
    }

    const settings = {
        useServerSideFLangNormalization: false,
        ...pickBy(formConfig.settings ?? {}, x => x !== undefined),
    };

    return {
        themes: formConfig.themes,
        build: formConfig.build || "manual",
        mode: formConfig.mode ?? "normal-build",
        settings: settings,
        dependencies:
            formConfig.dependencies != undefined
                ? {
                      generator: formConfig.dependencies.generator,
                      engine: formConfig.dependencies.engine,
                  }
                : undefined,
    };
}
